import Switch from "@/component/Switch";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DebugApis } from "@core/ability";
import IC_CLOSE_WIN from "@/assets/common/ic_close_win.svg?react";
import { IOptions, Select } from "@/component/Select";
import { SURPORT_MODELS } from "@/constants";
import { SURPORT_ENV } from "@/constants";
import appStore from "@/store/app.store";
import { debounce } from "@common/utils/common";
import { pageSpy, harbor } from '@/sentry/pageSpy';
import { message } from "antd";

interface IDebug {
  isOpen: boolean;
  onClose: () => void;
}

export default function DebugView(prop: IDebug) {
  const [envId, setEnvId] = useState('prod');
  const [isSave, setIsSave] = useState(false);
  const [isLocalConnect, setIsLocalConnect] = useState(false);
  const [isSyncPlaying, setIsSyncPlaying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isExporting = useRef(false);

  const [showSelect, setShowSelect] = useState(false);
  const [showEnvSelect, setShowEnvSelect] = useState(false);

  const asrModelId = appStore((state) => state.asrModelId);
  const asrModel = SURPORT_MODELS.find((item) => item.id === asrModelId);
  const defaultSamplingRate = localStorage.getItem("meetingVoiceSamplingRate") || "70";

  useEffect(() => {
    if (prop.isOpen) {
      init();
      handleSpyConsole();
    }
  }, [prop.isOpen]);

  if (!prop.isOpen) return null;

  const init = async () => {
    console.log('allowLocalConnect', localStorage.getItem("allowLocalConnect"));
    setIsLocalConnect(localStorage.getItem("allowLocalConnect") === "true");
    const env = await DebugApis.getEnv();
    console.log('getEnv = ', env);
    const currIsSave = await DebugApis.getRecordSave();
    const currIsSyncPlaying = await DebugApis.getSyncPlaying();
    setEnvId(env);
    setIsSave(currIsSave);
    setIsSyncPlaying(currIsSyncPlaying);
  };

  // const switchEnv = async (isProd: boolean) => {
  //   const res = await DebugApis.switchEnv(isProd);
  //   res && setIsProd(isProd);
  // };

  const handleCloseEnvModel = async (model: IOptions) => {
    if (model.id === envId) return;
    console.log('handleCloseEnvModel', model.id);
    const res = await DebugApis.setEnvName(model.id);
    if (res) {
      setEnvId(model.id);
    } else {
      // 兼容旧版本
      const res2 = await DebugApis.setEnvV1(model.id === 'prod');
      if (res2) {
        setEnvId(model.id === 'prod' ? model.id : 'test');
      } else {
        console.log('切换环境失败');
      }
    }
    setShowEnvSelect(false);
  };

  const handleShowEnvModel = () => {
    if (!showEnvSelect) {
      setShowEnvSelect(true);
    }
  };

  const switchSave = async () => {
    const res = await DebugApis.handleRecordSave(!isSave);
    res && setIsSave(!isSave);
  };
  const switchLocalConnect = async () => {
    localStorage.setItem("allowLocalConnect", isLocalConnect ? "false" : "true");
    setIsLocalConnect(!isLocalConnect);
  };

  const switchSyncPlaying = async () => {
    const res = await DebugApis.handleSyncPlaying(!isSyncPlaying);
    res && setIsSyncPlaying(!isSyncPlaying);
  };

  const handleSpyConsole = () => {
    pageSpy?.render();
  }

  const handleConsole = () => {
    DebugApis.openAppConsole(true);
  };

  const exportLog = async () => {
    if (isExporting.current) {
      return;
    }
    isExporting.current = true;
    const res = await DebugApis.exportLog();
    console.log(res);
    if (!res) {
      message.error('日志导出错误');
    } else {
      message.info(`已保存至：${res}`);
    }
    isExporting.current = false;
  };

  const uploadLog = async () => {
    const url = await harbor.onOfflineLog('upload');
    console.log(url);
    message.info('上传成功');
  }

  const handleCloseModel = (model: IOptions) => {
    if (model.id === asrModelId) return;
    appStore.setState({ asrModelId: model.id });
    setShowSelect(false);
  };

  const handleShowModelSelector = () => {
    if (!showSelect) {
      setShowSelect(true);
    }
  };

  const handleVoiceSamplingRateChange = (value: string) => {
    const formatValue = parseInt(value);
    let targetValue = formatValue.toString();
    if (!inputRef.current) return;
    if (formatValue < 1) {
      targetValue = "1";
    } else if (formatValue > 100) {
      targetValue = "100";
    }
    inputRef.current.value = targetValue;
    localStorage.setItem("meetingVoiceSamplingRate", targetValue);
  };

  const testSentry = () => {
    throw new Error('测试 Sentry 报错');
  }

  return createPortal(
    <div className="relative flex flex-col items-center bg-white w-80 h-100 rd-3 shadow-[0_4px_16px_0_#191D261F] px-6 pt-3 select-none">
      <IC_CLOSE_WIN
        className="absolute w-4 h-4 cursor-pointer top-2 right-4"
        onClick={(e) => {
          e.stopPropagation();
          prop.onClose();
        }}
      />
      <span className="text-size-4 text-mx-secondary font-bold mb-3">
        环境配置
      </span>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">运行环境</span>
        <div className="w-35 h-7 bg-#1A1D2614 rd-1.5 p-0.5 flex items-center justify-center cursor-pointer"
          onClick={handleShowEnvModel}>
          <span className="text-#666666 text-size-3 lh-6">{SURPORT_ENV.find((item) => item.id === envId)?.label}</span>
          {showEnvSelect && (
            <Select
              options={SURPORT_ENV}
              onChose={handleCloseEnvModel}
              onOutsideCb={() => setShowEnvSelect(false)}
            ></Select>
          )}
        </div>
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">ASR模型</span>
        <div
          className="w-35 h-7 bg-#1A1D2614 rd-1.5 p-0.5 flex items-center justify-center cursor-pointer"
          onClick={handleShowModelSelector}
        >
          <span className="text-#666666 text-size-3 lh-6">{asrModel?.label}</span>
          {showSelect && (
            <Select
              options={SURPORT_MODELS}
              onChose={handleCloseModel}
              onOutsideCb={() => setShowSelect(false)}
            ></Select>
          )}
        </div>
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">允许本地连接</span>
        <Switch
          className=""
          checked={isLocalConnect}
          size="small"
          onChange={switchLocalConnect}
        />
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">保存录音文件</span>
        <Switch
          className=""
          checked={isSave}
          size="small"
          onChange={switchSave}
        />
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">大板拾音边录边播</span>
        <Switch
          className=""
          checked={isSyncPlaying}
          size="small"
          onChange={switchSyncPlaying}
        />
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">声纹采样率</span>
        <input
          ref={inputRef}
          type="number"
          min="1"
          max="100"
          className="flex-1 text-right justify-end border-0 bg-transparent focus:outline-none h-6 placeholder:text-mx-quaternary mr-1"
          defaultValue={defaultSamplingRate}
          onChange={debounce((e) => {
            handleVoiceSamplingRateChange(
              (e as React.ChangeEvent<HTMLInputElement>).target.value
            );
          }, 500)}
        />
        %
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">控制台</span>
        <div
          className="w-14 h-7 rd-1.5 text-size-3 lh-7 text-align-center text-#4981F2 border-solid border-1 border-#4981F2 cursor-pointer"
          onClick={handleConsole}
        >
          打开
        </div>
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">日志管理</span>
        <div
          className="w-26 h-7 rd-1.5 text-size-3 lh-7 text-align-center text-#4981F2 border-solid border-1 border-#4981F2 cursor-pointer"
          onClick={uploadLog}
        >
          上传前端离线日志
        </div>
        <div
          className="w-14 h-7 rd-1.5 text-size-3 lh-7 text-align-center text-#4981F2 border-solid border-1 border-#4981F2 cursor-pointer"
          onClick={exportLog}
        >
          导出
        </div>
      </div>
      <div className="w-full h-14 flex justify-between items-center border-t-solid border-0.15 border-#ECEEF3">
        <span className="text-size-3.5 text-mx-secondary">sentry 测试</span>
        <div
          className="w-14 h-7 rd-1.5 text-size-3 lh-7 text-align-center text-#4981F2 border-solid border-1 border-#4981F2 cursor-pointer"
          onClick={testSentry}
        >
          测试
        </div>
      </div>
    </div>,
    document.body
  );
}
